import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTestReview } from '../../services/testsService';
import './ReviewScreen.css';

const normalizeQuestions = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.questions)
    ? data.questions
    : Array.isArray(data?.items)
    ? data.items
    : [];

  return list.map((q) => {
    const options = Object.entries(q.options || {}).map(([key, val]) => {
      const text = String(val);
      const letter = text.match(/^\(([a-zA-Z0-9]+)\)\s*/);
      return {
        id: letter ? letter[1].toUpperCase() : key,
        text: letter ? text.replace(letter[0], "") : text,
      };
    });

    return {
      id: q.question_id ?? q.id,
      text: q.question?.stem || q.text || q.stem || "",
      answer: q.answer ?? q.correct_option ?? q.solution?.correct_option,
      explanation: q.explanation ?? q.solution?.explanation ?? "",
      options,
    };
  });
};

const ReviewScreen = () => {
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const testName = sessionStorage.getItem("test_name") || "Economy - Sectional Test 2";

    useEffect(() => {
        const loadReview = async () => {
            setLoading(true);
            setError("");
            try {
                const userId = localStorage.getItem("user_id") || "";
                const testId = sessionStorage.getItem("test_id") || "";
                if (!testId) {
                    setError("No test selected for review.");
                    setLoading(false);
                    return;
                }
                const data = await fetchTestReview(testId, userId);
                setQuestions(normalizeQuestions(data));
            } catch (err) {
                console.error('Failed to load review:', err);
                setError("Failed to load review data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        loadReview();
    }, []);

    if (loading) {
        return <div className="review-container">Loading...</div>;
    }

    return (
        <div className="review-container">
            <div className="review-header">
                <div className="review-header-left">
                    <img src="/assets/images/logo/SVG/Logo-Org.svg" alt="Yooki" className="yooki-logo" />
                    <span className="separator">|</span>
                    <span className="exam-title-header">{testName}</span>
                </div>
                <button type="button" className="review-back-btn" onClick={() => navigate('/test/success')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                    Back
                </button>
            </div>

            <div className="review-body">
                <h2 className="review-title">Review Test</h2>
                {error ? (
                    <p className="review-error">{error}</p>
                ) : (
                <>
                <p className="review-subtitle">{questions.length} Questions</p>

                <div className="review-questions">
                    {questions.map((q, i) => (
                        <div key={q.id ?? i} className="review-question-card">
                            <div className="review-q-header">
                                <span className="review-q-num">Question {i + 1}</span>
                            </div>

                            <div className="review-q-text">
                                {q.text.split('\n').map((line, li) => (
                                    <p key={li}>{line}</p>
                                ))}
                            </div>

                            <div className="review-options">
                                {q.options.map(opt => {
                                    const isCorrect = opt.id === q.answer;
                                    return (
                                        <div key={opt.id} className={`review-option ${isCorrect ? 'correct' : ''}`}>
                                            <span className="review-opt-letter">({opt.id})</span>
                                            <span className="review-opt-text">{opt.text}</span>
                                            {isCorrect && (
                                                <span className="review-correct-badge">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                                                    Correct
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {q.explanation && (
                                <div className="review-explanation">
                                    <div className="review-exp-label">Explanation</div>
                                    <div dangerouslySetInnerHTML={{ __html: q.explanation }} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="review-footer">
                    <button type="button" className="review-home-btn" onClick={() => navigate('/test/success')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                        Back to Result
                    </button>
                </div>
                </>
                )}
            </div>
        </div>
    );
};

export default ReviewScreen;
